/**
 * PIN entry: 4 input boxes + numpad.
 * Light theme (settings): square boxes and keys, 12px radius.
 * Dark theme (lock/setup): Figma Section 16 — dark boxes, circular numpad keys.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import BackspaceIcon from "@/assets/hd-icons/backspace.svg?react";
import { PW_MANAGER_IGNORE_ATTRS } from "@common/lib/suppressPasswordManagers";

const BOX_SIZE = 64;
const PIN_LENGTH = 4;

const NUMPAD_KEYS = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["", "0", "⌫"],
];

export type PinEntryVariant = "enter" | "create" | "confirm";

export interface PinEntryProps {
    /** "Enter your PIN" vs "Create your 4-digit PIN" vs "Confirm your PIN" */
    variant?: PinEntryVariant;
    /** "dark" = lock/setup screen (circular keys, dark boxes); "light" = settings page */
    theme?: "dark" | "light";
    /** Current PIN digits (0–4). Controlled by parent or internal state. */
    value?: string;
    /** Called when value changes (each digit). */
    onChange?: (value: string) => void;
    /** Called when 4 digits entered (auto-submit). */
    onComplete?: (pin: string) => void;
    /** Show error state (e.g. wrong PIN). */
    hasError?: boolean;
    /** Disabled (e.g. cooldown). */
    disabled?: boolean;
    /** Inputs and numpad disabled, e.g. session expired. */
    readOnly?: boolean;
    /** Optional class for container */
    className?: string;
}

const variantTitles: Record<PinEntryVariant, string> = {
    enter: "Enter your PIN",
    create: "Create your 4-digit PIN",
    confirm: "Confirm your PIN",
};

function isDigit(key: string): boolean {
    return key.length === 1 && key >= "0" && key <= "9";
}

export default function PinEntry({
    variant = "enter",
    theme = "light",
    value: controlledValue,
    onChange,
    onComplete,
    hasError = false,
    disabled = false,
    readOnly = false,
    className = "",
}: PinEntryProps) {
    const isDark = theme === "dark";
    const [internalValue, setInternalValue] = useState("");
    const value = controlledValue !== undefined ? controlledValue : internalValue;
    const setValue = useCallback(
        (next: string) => {
            if (controlledValue === undefined) setInternalValue(next);
            onChange?.(next);
        },
        [controlledValue, onChange],
    );

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    /** Avoid calling onComplete twice for the same 4-digit entry (keydown + input can both fire). */
    const completionSentRef = useRef(false);

    const tryComplete = useCallback(
        (next: string) => {
            if (next.length !== PIN_LENGTH || completionSentRef.current) return;
            completionSentRef.current = true;
            onComplete?.(next);
        },
        [onComplete],
    );

    useEffect(() => {
        if (value.length < PIN_LENGTH) {
            completionSentRef.current = false;
        }
    }, [value.length]);

    // Focus the active PIN box on mount and when value length changes (e.g. after error clear)
    useEffect(() => {
        const idx = Math.min(value.length, PIN_LENGTH - 1);
        const el = inputRefs.current[idx];
        if (el && !readOnly && !disabled) {
            el.focus();
        }
    }, [value.length, readOnly, disabled]);

    const handleKey = useCallback(
        (digit: string) => {
            if (readOnly || disabled) return;
            if (digit === "⌫") {
                const prev = value.slice(0, -1);
                setValue(prev);
                const prevIdx = Math.min(prev.length, PIN_LENGTH - 1);
                inputRefs.current[prevIdx]?.focus();
                return;
            }
            if (digit === "" || value.length >= PIN_LENGTH) return;
            const next = value + digit;
            setValue(next);
            if (next.length === PIN_LENGTH) {
                tryComplete(next);
            } else {
                inputRefs.current[next.length]?.focus();
            }
        },
        [value, setValue, tryComplete, readOnly, disabled],
    );

    const handleInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>, indexFromMap: number) => {
            if (readOnly || disabled) return;
            // Use the actually focused input so mouse-click focus always matches the box we edit
            const focusedIndex = inputRefs.current.findIndex((el) => el === e.currentTarget);
            const index = focusedIndex >= 0 ? focusedIndex : indexFromMap;

            // Arrow keys: move focus between boxes
            if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                if (index > 0) inputRefs.current[index - 1]?.focus();
                return;
            }
            if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                if (index < PIN_LENGTH - 1) inputRefs.current[index + 1]?.focus();
                return;
            }

            if (e.key === "Backspace") {
                e.preventDefault();
                if (value.length === 0) return;
                // Focus is in an empty box (e.g. after "12" in box 2) — delete last digit and focus that box
                if (index >= value.length) {
                    const prev = value.slice(0, -1);
                    setValue(prev);
                    inputRefs.current[prev.length]?.focus();
                    return;
                }
                // Current box has the digit — remove the digit at this index and focus previous box
                const prev = value.slice(0, index) + value.slice(index + 1);
                setValue(prev);
                inputRefs.current[Math.max(0, index - 1)]?.focus();
                return;
            }
            if (isDigit(e.key)) {
                e.preventDefault();
                // Always splice digit at focused index (clicking box 0 and typing replaces that digit)
                const next = (value.slice(0, index) + e.key + value.slice(index + 1)).slice(0, PIN_LENGTH);
                setValue(next);
                if (next.length === PIN_LENGTH) {
                    tryComplete(next);
                } else {
                    inputRefs.current[next.length]?.focus();
                }
            }
        },
        [value, setValue, tryComplete, readOnly, disabled],
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
            if (readOnly || disabled) return;
            const raw = e.target.value;
            // Paste or multi-char: take only digits, max 4
            const digits = raw.replace(/\D/g, "").slice(0, PIN_LENGTH);
            if (digits.length === 0) {
                const prev = value.slice(0, index) + value.slice(index + 1);
                setValue(prev);
                return;
            }
            const before = value.slice(0, index);
            const after = value.slice(index + 1);
            const next = (before + digits + after).slice(0, PIN_LENGTH);
            setValue(next);
            if (next.length === PIN_LENGTH) {
                tryComplete(next);
            }
            const focusIdx = Math.min(index + digits.length, PIN_LENGTH - 1);
            inputRefs.current[focusIdx]?.focus();
        },
        [value, setValue, tryComplete, readOnly, disabled],
    );

    const digits = value.split("").concat(Array(PIN_LENGTH).fill("")).slice(0, PIN_LENGTH);

    return (
        <div className={`flex flex-col items-center ${className}`}>
            <h3
                className={isDark ? "text-white text-sm font-medium mb-6" : "text-[#101828] text-base font-medium mb-6"}
            >
                {variantTitles[variant]}
            </h3>

            {/* 4 focusable input boxes — keyboard and numpad both work */}
            <div className={`flex gap-3 mb-8 ${hasError ? "animate-shake" : ""}`} role="group" aria-label="PIN digits">
                {digits.map((d, i) => (
                    <input
                        key={i}
                        ref={(el) => {
                            inputRefs.current[i] = el;
                        }}
                        type="password"
                        inputMode="numeric"
                        autoComplete={i === 0 ? "one-time-code" : "off"}
                        // A device-unlock PIN is not an account credential — a password
                        // manager offering to save/fill it (especially on the shared-device
                        // lock screen) is noise. These boxes are `type="password"`, so the
                        // app-wide observer deliberately skips them (and never runs on the
                        // unauthenticated lock screen anyway); stamp the ignore attrs here
                        // directly. Static so they're present at first paint — no observer race.
                        {...PW_MANAGER_IGNORE_ATTRS}
                        maxLength={i === 0 ? PIN_LENGTH : 1}
                        value={d}
                        readOnly={readOnly}
                        disabled={disabled}
                        onKeyDown={(e) => handleInputKeyDown(e, i)}
                        onChange={(e) => handleInputChange(e, i)}
                        className={
                            isDark
                                ? `
									rounded-[8px] bg-[#2a3648] text-center text-2xl font-semibold text-white
									caret-transparent focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#131821]
									placeholder:opacity-0 border-0
									${hasError ? "ring-2 ring-red-400" : ""}
									${readOnly || disabled ? "cursor-default opacity-80" : ""}
								`
                                : `
									rounded-[8px] border-2 bg-white text-center text-2xl font-medium text-[#101828]
									caret-transparent focus:outline-none focus:ring-2 focus:ring-max-400 focus:ring-offset-2 focus:ring-offset-white
									placeholder:opacity-0
									${hasError ? "border-error-200" : "border-[#E4E7EC]"}
									${value.length === i && !readOnly ? "ring-2 ring-max-400 border-max-400" : ""}
									${readOnly || disabled ? "cursor-default opacity-80" : ""}
								`
                        }
                        style={{ width: BOX_SIZE, height: BOX_SIZE }}
                        aria-label={`PIN digit ${i + 1} of ${PIN_LENGTH}`}
                        tabIndex={readOnly || disabled ? -1 : 0}
                    />
                ))}
            </div>

            {/* Numpad — circular keys for both themes (Figma); dark = dark bg, light = light bg */}
            {!readOnly && (
                <div className={`grid grid-cols-3 ${isDark ? "gap-5" : "gap-5"}`}>
                    {NUMPAD_KEYS.flat().map((key, idx) =>
                        key === "" ? (
                            <div key={`empty-${idx}`} className="w-[62px] h-[62px]" />
                        ) : (
                            <button
                                key={key}
                                type="button"
                                disabled={disabled}
                                onClick={() => handleKey(key)}
                                className={
                                    isDark
                                        ? `
											w-[62px] h-[62px] rounded-full text-white text-[20.8px] font-medium
											bg-[#263142] border-none border-transparent shadow-none
											hover:bg-[#2a3648] focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#131821]
											disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center
										`
                                        : `
											w-[62px] h-[62px] rounded-full text-[#344054] text-xl font-medium
											bg-[#E4E7EC] border border-[#D0D5DD]
											hover:bg-[#D0D5DD] focus:outline-none focus:ring-2 focus:ring-max-400 focus:ring-offset-2 focus:ring-offset-white
											disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center
										`
                                }
                                aria-label={key === "⌫" ? "Backspace" : `Digit ${key}`}
                            >
                                {key === "⌫" && isDark ? (
                                    <BackspaceIcon className="w-6 h-6 text-white" aria-hidden />
                                ) : key === "⌫" ? (
                                    <BackspaceIcon className="w-6 h-6 text-[#344054]" aria-hidden />
                                ) : (
                                    key
                                )}
                            </button>
                        ),
                    )}
                </div>
            )}
        </div>
    );
}
